/**
 * 單據影像辨識(REQ-20260907003)—— 目前只做一件事:
 * 從一張**托運單照片**認出「託運單號」,帶回運費明細列。
 *
 * 這裡放的是**前後端共用**的常數與正規化 / 合理性判斷。刻意共用而不是各寫一份:
 * 後端要用它過濾模型的輸出,前端要用它擋掉送不出去的檔案 —— 兩邊的規則一旦分岔,
 * 使用者會看到「前端說可以、後端說不行」。
 *
 * ⚠️ **這裡沒有任何一家物流業者的樣板**(需求原文:「每家物流的單據格式會不同」)。
 * 沒有 `/^\d{4}-\d{4}$/` 這種嘉里大榮專用的正規表達式,也沒有業者清單對照表:
 * 寫死樣板的結果是「今天測的那一家會過、明天換一家就整個失效」,而且失效時是
 * **靜默的**(認不出來 = 沒有候選 = 看起來像模型不會讀)。
 * 這裡只做**與業者無關**的兩件事:把字元正規化、把明顯不可能是單號的字串濾掉。
 * 「哪一串才是單號」交給影像模型判斷,判錯了使用者看得到候選、可以不選。
 */

/**
 * 掃描用圖片的大小上限 8 MB。
 *
 * 挑這個數字:手機直出的照片約 2–5 MB,留 buffer;而前端送出前會先縮到長邊
 * 1600px / JPEG 0.8(約 200–500 KB),真正撞到上限的只有「縮圖失敗、原檔直送」
 * 那條 fallback。比單據附件的 10 MB 小是刻意的 —— 這張圖是要塞進 LLM 請求的
 * base64,越大越慢越貴,而它**不會被留存**,大小換不到任何東西。
 */
export const TRACKING_NO_SCAN_MAX_BYTES = 8 * 1024 * 1024;

/**
 * **只收圖片,不收 PDF。**
 *
 * 單據附件那一支(`isAllowedDocumentAttachmentMime`)是 `image/*` + PDF,這裡刻意窄一格:
 * 走的是影像模型的 `image_url`,吃不了 `application/pdf` —— 收下來的結果不是報錯,
 * 而是模型看不到任何東西、回一個空清單,使用者得到「傳了沒反應」的錯覺。
 * 要擋就在門口擋,並且說得出理由。
 */
export const TRACKING_NO_SCAN_ACCEPT = 'image/*';

export function isAllowedTrackingNoScanMime(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/** 單號長度下限。低於這個長度的字串幾乎一定是抄到別的東西(件數、樓層、日期) */
export const TRACKING_NO_MIN_LENGTH = 6;

/**
 * 單號長度上限 30,**對齊 `purchase_order_lines.trackingNo` 的 `VARCHAR(30)`**。
 * 超過 30 的字串就算真的印在單子上也存不進去(存檔時會被 zod 擋下),
 * 與其讓使用者點了候選、按了儲存才看到錯誤,不如在辨識這一步就濾掉。
 */
export const TRACKING_NO_MAX_LENGTH = 30;

/** 單號至少要有幾個數字。純英文字串(業者名、「CUSTOMER」之類)靠這條擋掉 */
const TRACKING_NO_MIN_DIGITS = 6;

/**
 * 正規化成「可以直接存進 `trackingNo` 的樣子」:
 * 全形轉半形 → 去掉空白 / 底線 → 只留英數與 `-` → 轉大寫。
 *
 * 為什麼要做這些:手寫單拍出來的辨識結果常常是 `６６１２ ８８０４`(全形 + 空白)
 * 或 `6612_8804`。這些差異對使用者來說是同一個單號,對 `WHERE trackingNo = ?`
 * 卻不是 —— 正規化留到查詢時才做就太晚了,DB 裡已經存了三種寫法。
 *
 * `-` 保留:多數業者的單號本身就帶連字號(`6612-8804`),拿掉會讓使用者
 * 對不上手上的單據。
 */
export function normalizeTrackingNo(raw: string): string {
  let out = '';
  for (const ch of raw) {
    const code = ch.codePointAt(0) ?? 0;
    // 全形 ！(U+FF01) ～ ～(U+FF5E) 對應半形 !(0x21) ～ ~(0x7E)
    const half = code >= 0xff01 && code <= 0xff5e ? String.fromCharCode(code - 0xfee0) : ch;
    if (/[0-9A-Za-z-]/.test(half)) out += half;
  }
  return out.toUpperCase();
}

/**
 * 這串**看起來**像不像託運單號。刻意寬鬆 —— 它的工作是擋掉模型明顯抄錯的東西
 * (客服專線、統編、金額、日期),不是判定單號真偽。
 *
 * 兩條規則都與業者無關:
 *  - 長度 6–30(下限擋碎片,上限對齊欄位長度)
 *  - 至少 6 個數字(擋掉純英文與「A-1」這種格子標記)
 */
export function isPlausibleTrackingNo(v: string): boolean {
  if (v.length < TRACKING_NO_MIN_LENGTH || v.length > TRACKING_NO_MAX_LENGTH) return false;
  const digits = v.replace(/[^0-9]/g, '').length;
  return digits >= TRACKING_NO_MIN_DIGITS;
}

/**
 * 辨識結果的狀態。**全部都是「正常回應」,不是例外** ——
 * 掃不到單號、AI 沒設定、模型不支援讀圖,對使用者來說都只是「這次沒帶入」,
 * 手打那一格永遠可用。用 500 表達這些情況只會讓畫面出現紅色系統錯誤。
 */
export type TrackingNoScanStatus =
  | 'ok'
  /** 圖看得到,但沒有任何一串通過合理性檢查 */
  | 'not_found'
  /** 系統設定裡的 AI Provider 未啟用 / 未設定完整 */
  | 'disabled'
  /** 目前設定的模型不支援影像輸入 */
  | 'unsupported_model'
  /** 撞到當日成本 / 次數上限 */
  | 'cost_cap'
  /** 其他失敗(連線、逾時、回應解析不了) */
  | 'error';

export interface TrackingNoCandidate {
  /** 已經過 `normalizeTrackingNo()` 的值,可直接填進欄位 */
  trackingNo: string;
  /** 模型認出的業者名(例「嘉里大榮」);認不出就是 null。**只是提示,不參與判斷** */
  carrier: string | null;
  confidence: 'high' | 'medium' | 'low';
}

export interface TrackingNoScanResult {
  status: TrackingNoScanStatus;
  /** `status !== 'ok'` 時必為空陣列 */
  candidates: TrackingNoCandidate[];
  /** 給使用者看的中文訊息;`ok` 時為 null */
  message: string | null;
}
